import {
  Table,
  Input,
  Button,
  Tag,
  Space,
  Typography,
  Popconfirm,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  UploadOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { CardFieldEditor } from "@/components/CardFieldEditor";
import { api } from "@/services/api";
import { resolveMediaUrl } from "@/utils/url";
import type { ApiResponse } from "@/types";

const { Text } = Typography;

export interface PreviewCard {
  title?: string;
  frontText: string;
  backText?: string;
  imageUrl?: string;
  isDuplicate?: boolean;
}

interface Props {
  cards: PreviewCard[];
  onChange: (cards: PreviewCard[]) => void;
  warnings?: string[];
}

export function CardPreviewTable({ cards, onChange, warnings = [] }: Props) {
  const updateCard = (
    index: number,
    field: keyof PreviewCard,
    value: string,
  ) => {
    const updated = cards.map((c, i) =>
      i === index ? { ...c, [field]: value || undefined } : c,
    );
    onChange(updated);
  };

  const removeCard = (index: number) => {
    onChange(cards.filter((_, i) => i !== index));
  };

  const handleImageUpload = async (index: number, file: File) => {
    // Client-side validation: image type + 2MB cap (backend enforces too)
    if (!file.type.startsWith("image/")) {
      message.error("Only image files are allowed");
      return;
    }
    const MAX_BYTES = 2 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      message.error("Image must be 2MB or smaller");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    const hide = message.loading(`Uploading card ${index + 1} image...`, 0);
    try {
      const res = await api.post<never, ApiResponse<{ url: string }>>(
        "/upload/image",
        formData,
        {
          headers: { "Content-Type": undefined },
          transformRequest: [(data: FormData) => data],
        },
      );
      if (res.data?.url) {
        const updated = cards.map((c, i) =>
          i === index ? { ...c, imageUrl: res.data.url } : c,
        );
        onChange(updated);
        message.success(`Card ${index + 1} image uploaded`);
      } else {
        message.error("Upload failed — server did not return an image URL");
      }
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "Image upload failed";
      message.error(msg);
    } finally {
      hide();
    }
  };

  const columns = [
    {
      title: "#",
      width: 48,
      align: "center" as const,
      render: (_: unknown, __: PreviewCard, i: number) => (
        <Text type="secondary">{i + 1}</Text>
      ),
    },
    {
      title: "Title",
      width: 160,
      render: (_: unknown, card: PreviewCard, i: number) => (
        <Input
          size="small"
          value={card.title ?? ""}
          maxLength={100}
          onChange={(e) => updateCard(i, "title", e.target.value)}
          placeholder="Card title"
        />
      ),
    },
    {
      title: "Front",
      width: "28%",
      render: (_: unknown, card: PreviewCard, i: number) => (
        <CardFieldEditor
          value={card.frontText}
          onChange={(v) => updateCard(i, "frontText", v)}
          placeholder="Front content (Markdown + LaTeX)"
          maxLength={500}
          rows={3}
        />
      ),
    },
    {
      title: "Back (optional)",
      width: "28%",
      render: (_: unknown, card: PreviewCard, i: number) => (
        <CardFieldEditor
          value={card.backText ?? ""}
          onChange={(v) => updateCard(i, "backText", v)}
          placeholder="Back content — leave empty for concept card"
          maxLength={500}
          rows={3}
          optional
        />
      ),
    },
    {
      title: "Image",
      width: 90,
      align: "center" as const,
      render: (_: unknown, card: PreviewCard, i: number) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
          }}
        >
          {card.imageUrl ? (
            <div style={{ position: "relative", display: "inline-block" }}>
              <img
                src={resolveMediaUrl(card.imageUrl)}
                alt="card"
                style={{
                  width: 48,
                  height: 48,
                  objectFit: "cover",
                  borderRadius: 4,
                  border: "1px solid #e2e8f0",
                }}
              />
              <CloseCircleOutlined
                onClick={() => {
                  const updated = cards.map((c, idx) =>
                    idx === i ? { ...c, imageUrl: undefined } : c,
                  );
                  onChange(updated);
                }}
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  fontSize: 14,
                  color: "#ff4d4f",
                  cursor: "pointer",
                  background: "#fff",
                  borderRadius: "50%",
                }}
              />
            </div>
          ) : (
            <Upload
              accept="image/*"
              showUploadList={false}
              maxCount={1}
              beforeUpload={(file) => {
                handleImageUpload(i, file);
                return false;
              }}
            >
              <Button size="small" icon={<UploadOutlined />} />
            </Upload>
          )}
        </div>
      ),
    },
    {
      title: "",
      width: 56,
      align: "center" as const,
      render: (_: unknown, card: PreviewCard, i: number) => (
        <Space direction="vertical" align="center">
          {card.isDuplicate && <Tag color="orange">Dup</Tag>}
          <Popconfirm
            title="Delete card"
            description="Are you sure you want to delete this card?"
            onConfirm={() => removeCard(i)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {warnings
        .filter((w) => !w.startsWith("DUPLICATE_DECK"))
        .map((w, i) => (
          <div
            key={i}
            style={{ color: "#d97706", marginBottom: 8, fontSize: 13 }}
          >
            ⚠ {w}
          </div>
        ))}
      <Table
        dataSource={cards.map((c, i) => ({ ...c, key: i }))}
        columns={columns}
        pagination={false}
        size="small"
        bordered
        scroll={{ x: 800 }}
        style={{ tableLayout: "fixed" }}
      />
    </div>
  );
}
